
import { onBeforeUnmount, onMounted } from 'vue';

const MOBILE_QUERY = '(max-width: 768px)';
const TAB_BARS = [
  '.c-activity-nav',
  '.c-directory-nav',
  '.c-glows-nav',
  '.c-orgs-nav',
  '.c-requests-nav',
  '.c-resources-nav',
  '.c-stickers-nav',
  '.c-users-nav',
  '.mc-page .filter_container',
].join(',');
const ACTIVE = '[class*="-link--active"], .router-link-exact-active, .router-link-active, '
  + '.is-active, .filter-active';

// Edge fades tell the user there are more tabs off-screen (see `.can-scroll-*` in _cms-theme.scss).
function updateEdges(bar: HTMLElement): void {
  const max = bar.scrollWidth - bar.clientWidth;
  bar.classList.toggle('can-scroll-left', bar.scrollLeft > 4);
  bar.classList.toggle('can-scroll-right', max > 4 && bar.scrollLeft < max - 4);
}

function centerActive(bar: HTMLElement): void {
  const active = bar.querySelector<HTMLElement>(ACTIVE);
  if (!active || bar.dataset.activeKey === active.textContent) {
    updateEdges(bar);
    return;
  }
  bar.dataset.activeKey = active.textContent || '';
  const target = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
  bar.scrollLeft = Math.max(0, target);
  updateEdges(bar);
}

// Tab bars on phones scroll sideways: bring the active tab into view and show edge fades.
export function useScrollableTabs(): void {
  let observer: MutationObserver | null = null;
  let query: MediaQueryList | null = null;
  let frame = 0;

  const run = () => {
    frame = 0;
    if (!query?.matches) return;
    document.querySelectorAll<HTMLElement>(TAB_BARS).forEach(centerActive);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(run);
  };
  // `scroll` does not bubble, so listen in the capture phase
  const onScroll = (event: Event) => {
    const el = event.target as HTMLElement;
    if (el instanceof HTMLElement && el.matches?.(TAB_BARS)) updateEdges(el);
  };

  onMounted(() => {
    query = window.matchMedia(MOBILE_QUERY);
    query.addEventListener('change', schedule);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', schedule);
    observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['class'],
    });
    schedule();
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    query?.removeEventListener('change', schedule);
    document.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', schedule);
    if (frame) cancelAnimationFrame(frame);
  });
}
